export const consultingServicePages = [
  {
    slug: 'wix-custom-website-development',
    path: '/consulting/services/wix-custom-website-development',
    seoTitle: 'Wix & Custom Website Development | GoldTech Consulting',
    metaDescription: 'Wix and custom website development with conversion-focused UX, technical reviews, and performance improvements.',
    eyebrow: 'Web development',
    h1: 'Wix & Custom Website Development',
    intro:
      'We build Wix and custom websites that look premium, load fast, and convert visitors into leads.',
    sections: [
      {
        title: 'Who it is for',
        items: [
          'Teams launching a new Wix or custom site',
          'Brands ready for a redesign with clearer conversion paths',
          'Owners who want a technical review before scaling traffic'
        ]
      },
      {
        title: 'Problems we solve',
        items: [
          'Websites that look good but do not convert',
          'Slow load times and inconsistent UX',
          'Sites that are difficult to update or scale'
        ]
      },
      {
        title: 'Tools and platforms',
        items: [
          'Wix Editor, Wix Studio, and custom builds',
          'Content structure and UX conversion flows',
          'Performance, analytics, and technical QA'
        ]
      },
      {
        title: 'Outcomes you can expect',
        items: [
          'A website you are proud to share',
          'Clear conversion paths and stronger lead flow',
          'Improved performance and site reliability'
        ]
      }
    ],
    cta: {
      text: 'Build or improve your site',
      href: '/consulting#contact'
    }
  },
  {
    slug: 'systems-integration-consulting',
    path: '/consulting/services/systems-integration-consulting',
    seoTitle: 'Systems & Integration Consulting | GoldTech Consulting',
    metaDescription: 'Systems and integration consulting that connects your website, CRM, and tools with reliable workflows and data flows.',
    eyebrow: 'Integrations',
    h1: 'Systems & Integration Consulting',
    intro:
      'We connect the tools you already use so data flows cleanly between your website, CRM, and operations systems.',
    sections: [
      {
        title: 'Who it is for',
        items: [
          'Teams juggling multiple tools with no single source of truth',
          'Businesses with website, CRM, and billing data out of sync',
          'Organizations needing reliable API connections'
        ]
      },
      {
        title: 'Problems we solve',
        items: [
          'Data mismatches across platforms',
          'Manual exports and file imports',
          'Unreliable syncs and broken automations'
        ]
      },
      {
        title: 'Tools and platforms',
        items: [
          'Wix CRM, HubSpot, and custom CRM integrations',
          'API integrations across Wix, CRMs, and booking tools',
          'Monitoring and logging for reliability'
        ]
      },
      {
        title: 'Outcomes you can expect',
        items: [
          'One source of truth for customer data',
          'Reliable syncs and automation workflows',
          'Faster operations with fewer manual fixes'
        ]
      }
    ],
    cta: {
      text: 'Connect your systems',
      href: '/consulting#contact'
    }
  },
  {
    slug: 'custom-software-solutions',
    path: '/consulting/services/custom-software-solutions',
    seoTitle: 'Custom Software Solutions | GoldTech Consulting',
    metaDescription: 'Custom software solutions tailored to your workflows, data, and tools for measurable operational gains.',
    eyebrow: 'Custom solutions',
    h1: 'Custom Software Solutions',
    intro:
      'When off-the-shelf tools are not enough, we build custom software, automations, and integrations that fit your workflows.',
    sections: [
      {
        title: 'Who it is for',
        items: [
          'Businesses with unique workflows and edge cases',
          'Teams that outgrew plug-and-play automation',
          'Leaders who need custom reporting and alerts'
        ]
      },
      {
        title: 'Problems we solve',
        items: [
          'Automation gaps that require custom logic',
          'Missing connections between tools',
          'Slow or unreliable workflows'
        ]
      },
      {
        title: 'Tools and platforms',
        items: [
          'Custom scripts and API integrations',
          'AWS services for scalable automation',
          'Monitoring and observability for uptime'
        ]
      },
      {
        title: 'Outcomes you can expect',
        items: [
          'Automation built around your exact needs',
          'Higher reliability and fewer manual fixes',
          'Clear dashboards and reporting'
        ]
      }
    ],
    cta: {
      text: 'Build a custom solution',
      href: '/consulting#contact'
    }
  },
  {
    slug: 'business-technology-advisory',
    path: '/consulting/services/business-technology-advisory',
    seoTitle: 'Business & Technology Advisory | GoldTech Consulting',
    metaDescription: 'Business and technology advisory to prioritize systems, streamline operations, and build a clear execution roadmap.',
    eyebrow: 'Advisory services',
    h1: 'Business & Technology Advisory',
    intro:
      'We help you prioritize systems, reduce operational friction, and build a practical plan for scaling your business.',
    sections: [
      {
        title: 'Who it is for',
        items: [
          'Founders who need clarity on where to invest in tech',
          'Teams dealing with inefficient operations and handoffs',
          'Leaders preparing for growth or a major change'
        ]
      },
      {
        title: 'Problems we solve',
        items: [
          'Unclear priorities across systems and tools',
          'Manual processes that slow delivery',
          'No roadmap for upgrades and automation'
        ]
      },
      {
        title: 'Tools and platforms',
        items: [
          'Systems audits and technology roadmaps',
          'Workflow mapping and process optimization',
          'Vendor evaluation and implementation guidance'
        ]
      },
      {
        title: 'Outcomes you can expect',
        items: [
          'A clear plan tied to business goals',
          'Faster execution with fewer bottlenecks',
          'Better alignment between teams and tools'
        ]
      }
    ],
    cta: {
      text: 'Get advisory support',
      href: '/consulting#contact'
    }
  }
];

export const consultingPlatformPages = [
  {
    slug: 'wix-automation',
    path: '/consulting/wix-automation',
    seoTitle: 'Wix Automation Consultant | GoldTech Consulting',
    metaDescription: 'Wix automation consulting to streamline lead capture, client intake, and follow-ups with Wix, Zapier, and APIs.',
    eyebrow: 'Wix automation',
    h1: 'Wix Automation Consulting',
    intro:
      'We build Wix automations that reduce manual work, with site guidance for single-page or multi-page Wix builds.',
    sections: [
      {
        title: 'Who it is for',
        items: [
          'Wix site owners who want faster workflows',
          'Teams managing Wix forms, bookings, and payments',
          'Businesses planning a Wix site build with automation baked in'
        ]
      },
      {
        title: 'What we automate',
        items: [
          'Lead capture and routing',
          'Booking and payment workflows',
          'CRM updates and follow-up sequences'
        ]
      },
      {
        title: 'Tools we use',
        items: [
          'Wix Automations and Wix CRM',
          'Zapier and custom API integrations',
          'AWS services for reliability'
        ]
      },
      {
        title: 'Outcomes you can expect',
        items: [
          'Fewer manual steps for your team',
          'Cleaner data and faster follow-ups',
          'A Wix site foundation ready to scale'
        ]
      }
    ],
    cta: {
      text: 'Automate your Wix workflows',
      href: '/consulting#contact'
    }
  },
  {
    slug: 'wix-crm-integration',
    path: '/consulting/wix-crm-integration',
    seoTitle: 'Wix CRM Integration Services | GoldTech Consulting',
    metaDescription: 'Connect Wix forms, bookings, and payments to your CRM with reliable Wix CRM integration services.',
    eyebrow: 'Wix CRM',
    h1: 'Wix CRM Integration',
    intro:
      'We connect Wix CRM to your lead sources and marketing tools so every client record stays accurate and up to date.',
    sections: [
      {
        title: 'Who it is for',
        items: [
          'Teams using Wix forms and Wix Bookings',
          'Businesses with multiple lead sources',
          'Operations managers who need clean CRM data'
        ]
      },
      {
        title: 'What we integrate',
        items: [
          'Wix Forms and Wix Bookings',
          'Email platforms and SMS tools',
          'Sales pipelines and reporting'
        ]
      },
      {
        title: 'Tools we use',
        items: [
          'Wix CRM and Wix Automations',
          'Zapier and API workflows',
          'AWS for secure processing'
        ]
      },
      {
        title: 'Outcomes you can expect',
        items: [
          'Faster lead response times',
          'Consistent CRM data',
          'Better visibility across your funnel'
        ]
      }
    ],
    cta: {
      text: 'Connect Wix to your CRM',
      href: '/consulting#contact'
    }
  },
  {
    slug: 'wix-booking-automation',
    path: '/consulting/wix-booking-automation',
    seoTitle: 'Wix Booking Automation | GoldTech Consulting',
    metaDescription: 'Automate Wix Bookings with confirmations, reminders, and CRM updates for a smoother client experience.',
    eyebrow: 'Wix Bookings',
    h1: 'Wix Booking Automation',
    intro:
      'We automate Wix Bookings so confirmations, reminders, and follow-ups happen without manual effort.',
    sections: [
      {
        title: 'Who it is for',
        items: [
          'Service businesses using Wix Bookings',
          'Teams managing manual appointment follow-ups',
          'Owners who want fewer no-shows'
        ]
      },
      {
        title: 'What we automate',
        items: [
          'Booking confirmations and reminders',
          'Payment and invoice workflows',
          'CRM updates and client onboarding'
        ]
      },
      {
        title: 'Tools we use',
        items: [
          'Wix Bookings and Wix Automations',
          'Zapier, email, and SMS integrations',
          'AWS services for custom workflows'
        ]
      },
      {
        title: 'Outcomes you can expect',
        items: [
          'Fewer missed appointments',
          'Better client communication',
          'More time for delivery'
        ]
      }
    ],
    cta: {
      text: 'Automate Wix bookings',
      href: '/consulting#contact'
    }
  }
];

export const consultingIndustryPages = [
  {
    slug: 'for-coaches',
    path: '/consulting/for-coaches',
    seoTitle: 'Automation for Coaches | GoldTech Consulting',
    metaDescription: 'Automation for coaches that streamlines client intake, scheduling, and follow-ups so you can focus on coaching.',
    eyebrow: 'For coaches',
    h1: 'Automation for Coaches',
    intro:
      'We help coaches automate client intake, scheduling, and CRM updates so you can spend more time delivering results.',
    sections: [
      {
        title: 'Common challenges',
        items: [
          'Manual intake forms and scattered client data',
          'Scheduling chaos and missed follow-ups',
          'No clear view of client progress'
        ]
      },
      {
        title: 'What we deliver',
        items: [
          'Wix and custom websites that support client intake',
          'Systems and integrations for scheduling and CRM',
          'Custom automation for onboarding and follow-ups',
          'Advisory guidance to prioritize what to automate first'
        ]
      },
      {
        title: 'Tools we use',
        items: [
          'Wix, CRMs, and scheduling tools',
          'Zapier and custom APIs',
          'AWS for secure automation'
        ]
      }
    ],
    cta: {
      text: 'Automate your coaching workflow',
      href: '/consulting#contact'
    }
  },
  {
    slug: 'for-consultants',
    path: '/consulting/for-consultants',
    seoTitle: 'Automation for Consultants | GoldTech Consulting',
    metaDescription: 'Automation for consultants that reduces admin work, improves client intake, and keeps projects moving.',
    eyebrow: 'For consultants',
    h1: 'Automation for Consultants',
    intro:
      'We automate the admin and client intake tasks that slow down consulting teams, so you can focus on delivery.',
    sections: [
      {
        title: 'Common challenges',
        items: [
          'Inconsistent intake and discovery workflows',
          'Manual proposals, invoices, and follow-ups',
          'No central system for client data'
        ]
      },
      {
        title: 'What we deliver',
        items: [
          'Wix and custom sites that convert inbound leads',
          'Systems and integrations for proposals and payments',
          'Custom software to streamline intake and delivery',
          'Business and technology advisory for scale'
        ]
      },
      {
        title: 'Tools we use',
        items: [
          'Wix forms, CRM, and automation tools',
          'Zapier and custom integrations',
          'AWS services for automation'
        ]
      }
    ],
    cta: {
      text: 'Streamline your consulting ops',
      href: '/consulting#contact'
    }
  },
  {
    slug: 'for-real-estate-teams',
    path: '/consulting/for-real-estate-teams',
    seoTitle: 'Automation for Real Estate Teams | GoldTech Consulting',
    metaDescription: 'Automation for real estate teams that accelerates lead response, follow-ups, and pipeline updates.',
    eyebrow: 'For real estate',
    h1: 'Automation for Real Estate Teams',
    intro:
      'We build automation that helps real estate teams respond faster, follow up consistently, and keep pipelines accurate.',
    sections: [
      {
        title: 'Common challenges',
        items: [
          'Slow lead response and missed follow-ups',
          'Manual updates across multiple tools',
          'No visibility into pipeline stages'
        ]
      },
      {
        title: 'What we deliver',
        items: [
          'Wix and custom sites that capture listing inquiries',
          'Systems and integrations for lead routing',
          'Custom software for pipeline visibility',
          'Advisory support for workflow optimization'
        ]
      },
      {
        title: 'Tools we use',
        items: [
          'CRM tools and lead sources',
          'Automation with Zapier or APIs',
          'AWS services for scale'
        ]
      }
    ],
    cta: {
      text: 'Automate your lead pipeline',
      href: '/consulting#contact'
    }
  },
  {
    slug: 'for-speakers',
    path: '/consulting/for-speakers',
    seoTitle: 'Automation for Speakers | GoldTech Consulting',
    metaDescription: 'Automation for speakers that streamlines booking requests, follow-ups, and CRM updates.',
    eyebrow: 'For speakers',
    h1: 'Automation for Speakers',
    intro:
      'We automate booking requests, intake, and follow-ups so speakers can focus on stage time, not admin work.',
    sections: [
      {
        title: 'Common challenges',
        items: [
          'Manual booking requests and scheduling',
          'Lost leads across email and forms',
          'No clear reporting on inquiries'
        ]
      },
      {
        title: 'What we deliver',
        items: [
          'Wix and custom sites that support booking inquiries',
          'Systems and integrations for calendar and CRM',
          'Custom automation for follow-up and reporting',
          'Advisory guidance for a scalable booking funnel'
        ]
      },
      {
        title: 'Tools we use',
        items: [
          'Wix sites and booking tools',
          'Zapier and custom API workflows',
          'AWS services for reliability'
        ]
      }
    ],
    cta: {
      text: 'Streamline speaking bookings',
      href: '/consulting#contact'
    }
  }
];

export const getConsultingPageBySlug = (pages, slug) =>
  pages.find((page) => page.slug === slug);
